
import { useState } from "react";
import { SearchResult } from "@/types/client-search";
import { AdminSection } from "./AdminSection";
import { MainDashboard } from "./MainDashboard";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useToast } from "@/hooks/use-toast";
import { Spinner } from "@/components/ui/spinner";
import { DashboardWelcome } from "./DashboardWelcome";
import { useNavigate } from "react-router-dom";

interface DashboardContentProps {
  isAdmin: boolean;
  selectedClientName?: string;
  selectedClientId?: string;
}

export const DashboardContent = ({
  isAdmin,
  selectedClientName,
  selectedClientId,
}: DashboardContentProps) => {
  const [showSearch] = useState(false);
  const { firstName, lastName, loading: profileLoading, error } = useUserProfile();
  const { userId, loading: sessionLoading } = useAuthSession();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Add a very visible deployment verification log
  console.log("🚀 VERCEL DEPLOYMENT VERIFICATION - DashboardContent loaded at:", new Date().toISOString());
  console.log("📦 Build Version:", "2024-01-27-B");

  const handleClientSelect = (client: SearchResult) => {
    console.log("Selected client:", client);
  };

  console.log("DashboardContent rendering with:", { 
    isAdmin, 
    selectedClientName, 
    firstName, 
    lastName, 
    profileLoading, 
    sessionLoading,
    userId,
    error 
  });

  if (sessionLoading || profileLoading) {
    console.log("Loading state:", { sessionLoading, profileLoading });
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (error?.message?.includes("JWT") || error?.message?.includes("user_not_found")) {
    console.error("Authentication error:", error);
    toast({
      variant: "destructive",
      title: "Session expired",
      description: "Please sign in again."
    });
    navigate("/auth");
    return null;
  }

  if (error) {
    console.error("Profile error:", error);
    toast({
      variant: "destructive",
      title: "Error loading profile",
      description: "There was an error loading your profile data. Please try refreshing the page."
    });
    return (
      <div className="p-8 text-center text-red-500">
        Error loading profile data. Please refresh the page.
      </div>
    );
  }

  if (!userId) {
    console.log("No user ID found");
    return null;
  }

  return (
    <div className="p-8">
      <DashboardWelcome
        firstName={firstName}
        lastName={lastName}
        selectedClientName={selectedClientName || ""}
      />
      
      {isAdmin && !selectedClientName && (
        <AdminSection 
          showSearch={showSearch}
          onClientSelect={handleClientSelect}
        />
      )}

      {!selectedClientName && (
        <MainDashboard 
          firstName={firstName}
          lastName={lastName}
        />
      )}
    </div>
  );
};
